// ShareCardGenerator — desenha o card compartilhável em Canvas HTML5.
// Sem bibliotecas externas de imagem. Retorna Blob PNG.
//
// Templates suportados:
//   contemplacao (padrão, navy sólido)
//   esperanca (navy + gradiente dourado)
//   oracao (navy escuro + círculo dourado)
//   perdao (vinho profundo)
//   familia (verde-pinho terra)
//   paz (azul-cinza calmo)
//   proposito (roxo profundo)
//
// Formatos:
//   story (1080x1920)
//   feed  (1080x1080)

const NAVY = "#0D1B2A";
const CREAM = "#F0EAD6";
const GOLD = "#C9A961";
const DIM = "rgba(240, 234, 214, 0.55)";
const RULE = "rgba(201, 169, 97, 0.55)";

// Paleta de fundo por template (P4). Mesma tipografia, mesma cor de texto,
// mesma cor de accent (gold). Só o fundo muda, com sutileza pastoral.
const TEMPLATE_BG = {
  contemplacao: NAVY,
  esperanca: NAVY,
  oracao: "#080F1A",
  perdao: "#2A1620",     // vinho profundo (Ef 4.32)
  familia: "#1F2B24",    // verde-pinho terra
  paz: "#172531",        // azul-cinza sereno (Sl 46.10)
  proposito: "#1E1738",  // roxo profundo (1Pe 2.9)
};

const FONTS = {
  serif: 'Fraunces, "Playfair Display", Georgia, serif',
  sans: 'Manrope, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
};

function wrapText(ctx, text, x, y, maxWidth, lineHeight, maxLines = null) {
  const words = (text || "").split(/\s+/);
  const lines = [];
  let current = "";
  words.forEach((w) => {
    const test = current ? current + " " + w : w;
    if (ctx.measureText(test).width <= maxWidth) {
      current = test;
    } else {
      if (current) lines.push(current);
      current = w;
    }
  });
  if (current) lines.push(current);
  const actual = maxLines ? lines.slice(0, maxLines) : lines;
  if (maxLines && lines.length > maxLines) {
    const last = actual[actual.length - 1];
    actual[actual.length - 1] = last.replace(/[,.;:\s]*$/, "") + "…";
  }
  actual.forEach((line, i) => ctx.fillText(line, x, y + i * lineHeight));
  return y + actual.length * lineHeight;
}

function drawLogo(ctx, x, y) {
  // Logo textual "CM" em serif dourado (pequeno, discreto).
  ctx.save();
  ctx.fillStyle = GOLD;
  ctx.font = `600 42px ${FONTS.serif}`;
  ctx.textBaseline = "top";
  ctx.fillText("CM", x, y);
  ctx.fillStyle = DIM;
  ctx.font = `400 20px ${FONTS.sans}`;
  ctx.fillText("Bíblia com Propósito", x + 62, y + 12);
  ctx.restore();
}

function drawFooter(ctx, W, H, sharedBy) {
  ctx.save();
  ctx.textAlign = "center";
  ctx.fillStyle = DIM;
  ctx.font = `400 24px ${FONTS.sans}`;
  ctx.textBaseline = "alphabetic";
  ctx.fillText("app.cemedeiros.com.br", W / 2, H - 100);
  if (sharedBy) {
    ctx.font = `500 22px ${FONTS.sans}`;
    ctx.fillStyle = DIM;
    ctx.fillText(`Compartilhado por ${sharedBy}`, W / 2, H - 60);
  }
  ctx.restore();
}

async function ensureFontsReady() {
  if (typeof document !== "undefined" && document.fonts && document.fonts.ready) {
    try {
      await document.fonts.ready;
    } catch {
      /* ignore */
    }
  }
}

/**
 * @param {object} opts
 * @param {"story"|"feed"} opts.format
 * @param {"contemplacao"|"esperanca"|"oracao"|"perdao"|"familia"|"paz"|"proposito"} [opts.template]
 * @param {object} opts.data { title, main_phrase, verse, verse_ref, theme }
 * @param {string} [opts.sharedBy]
 * @returns {Promise<Blob>}
 */
export async function generateShareCard({ format, template = "contemplacao", data = {}, sharedBy }) {
  await ensureFontsReady();

  const W = 1080;
  const H = format === "feed" ? 1080 : 1920;

  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");

  // ---------- background ----------
  ctx.fillStyle = TEMPLATE_BG[template] || NAVY;
  ctx.fillRect(0, 0, W, H);

  if (template === "esperanca") {
    // gradiente suave dourado no centro
    const grad = ctx.createRadialGradient(W / 2, H / 2, 0, W / 2, H / 2, W * 0.7);
    grad.addColorStop(0, "rgba(201, 169, 97, 0.10)");
    grad.addColorStop(1, "rgba(13, 27, 42, 0)");
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, W, H);
  }

  if (template === "oracao") {
    // círculo sutil dourado
    ctx.save();
    ctx.beginPath();
    ctx.arc(W / 2, H / 2, W * 0.35, 0, Math.PI * 2);
    ctx.strokeStyle = "rgba(201, 169, 97, 0.15)";
    ctx.lineWidth = 2;
    ctx.stroke();
    ctx.restore();
  }

  if (template === "paz") {
    // linha horizontal serena (águas paradas de Sl 23)
    ctx.save();
    ctx.strokeStyle = "rgba(201, 169, 97, 0.10)";
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(0, H * 0.62);
    ctx.lineTo(W, H * 0.62);
    ctx.stroke();
    ctx.restore();
  }

  if (template === "familia") {
    // pequeno rule vertical à esquerda (raiz/tronco)
    ctx.save();
    ctx.fillStyle = "rgba(201, 169, 97, 0.10)";
    ctx.fillRect(40, H * 0.3, 3, H * 0.4);
    ctx.restore();
  }

  if (template === "proposito") {
    // 3 pontos dourados sutis no canto superior direito (norte/estrela)
    ctx.save();
    ctx.fillStyle = "rgba(201, 169, 97, 0.22)";
    [[W - 120, 120], [W - 160, 170], [W - 90, 190]].forEach(([px, py]) => {
      ctx.beginPath();
      ctx.arc(px, py, 4, 0, Math.PI * 2);
      ctx.fill();
    });
    ctx.restore();
  }

  if (template === "perdao") {
    // arco sutil na base (reconciliação, abraço)
    ctx.save();
    ctx.strokeStyle = "rgba(201, 169, 97, 0.14)";
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(W / 2, H + 100, W * 0.55, Math.PI, Math.PI * 2);
    ctx.stroke();
    ctx.restore();
  }

  // ---------- logo ----------
  drawLogo(ctx, 70, 70);

  // ---------- content ----------
  const padX = 96;
  const contentW = W - padX * 2;
  let cursorY = format === "feed" ? 260 : 420;

  // Fase C.1 — badge do tema semanal ativo (landing).
  // Sobrescreve o "theme" antigo se houver weekly_theme_name.
  const weeklyBadge = (data.weekly_theme_name || "").toString().trim();
  if (weeklyBadge) {
    ctx.fillStyle = GOLD;
    ctx.font = `600 26px ${FONTS.sans}`;
    ctx.textAlign = "left";
    ctx.textBaseline = "top";
    ctx.fillText(`✦ ${weeklyBadge.toUpperCase()}`, padX, cursorY);
    cursorY += 60;
  } else if (data.theme) {
    // fallback legado: usa `theme` local
    ctx.fillStyle = GOLD;
    ctx.font = `600 26px ${FONTS.sans}`;
    ctx.textAlign = "left";
    ctx.textBaseline = "top";
    ctx.fillText(`✦ ${data.theme.toUpperCase()}`, padX, cursorY);
    cursorY += 60;
  }

  // main phrase
  const mainText = data.main_phrase || data.title || "";
  if (mainText) {
    ctx.fillStyle = CREAM;
    ctx.font = `500 ${format === "feed" ? 56 : 66}px ${FONTS.serif}`;
    ctx.textAlign = "left";
    const quoted = `"${mainText}"`;
    const lineHeight = format === "feed" ? 76 : 92;
    const maxLines = format === "feed" ? 6 : 10;
    cursorY = wrapText(ctx, quoted, padX, cursorY, contentW, lineHeight, maxLines) + 40;
  }

  // gold rule
  ctx.save();
  ctx.strokeStyle = RULE;
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(padX, cursorY);
  ctx.lineTo(padX + 200, cursorY);
  ctx.stroke();
  ctx.restore();
  cursorY += 50;

  // verse ref
  if (data.verse_ref) {
    ctx.fillStyle = GOLD;
    ctx.font = `500 36px ${FONTS.sans}`;
    ctx.textAlign = "left";
    ctx.fillText(data.verse_ref, padX, cursorY);
    cursorY += 60;
  }

  // ---------- footer ----------
  drawFooter(ctx, W, H, sharedBy);

  return new Promise((resolve) => canvas.toBlob((b) => resolve(b), "image/png", 0.95));
}
